import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft,
  ArrowRight,
  Calendar,
  User,
  Clock,
  Share2,
  Linkedin,
  Twitter,
  Facebook,
  Link as LinkIcon
} from 'lucide-react';
import PublicHeader from './components/PublicHeader';
import PublicFooter from './components/PublicFooter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Default posts for demonstration
  const defaultPosts = {
    'future-digital-marketing-2025': {
      id: '1',
      title: 'The Future of Digital Marketing: Trends to Watch in 2025',
      slug: 'future-digital-marketing-2025',
      excerpt: 'Explore the key digital marketing trends that will shape business strategies in 2025.',
      content: `
        <p>The digital marketing landscape continues to evolve at an unprecedented pace. As we move through 2025, several key trends are reshaping how businesses connect with their audiences and drive meaningful results.</p>

        <h2>1. AI-Powered Personalization</h2>
        <p>Artificial intelligence is no longer just a buzzword—it's become essential for delivering personalized experiences at scale. From dynamic content recommendations to predictive customer journey mapping, AI enables marketers to create truly individualized interactions that resonate with each customer.</p>

        <h2>2. Privacy-First Marketing</h2>
        <p>With the continued deprecation of third-party cookies and increasing privacy regulations, marketers must adapt their strategies to rely more heavily on first-party data and contextual targeting. Building direct relationships with customers has never been more important.</p>

        <h2>3. Immersive Experiences</h2>
        <p>Augmented reality (AR) and virtual reality (VR) are moving from novelty to necessity. Brands that can create immersive experiences—whether virtual product demonstrations, AR try-on features, or interactive 3D content—will stand out in crowded markets.</p>

        <h2>4. Sustainable Marketing</h2>
        <p>Consumers increasingly expect brands to demonstrate genuine commitment to sustainability. This extends beyond messaging to include sustainable marketing practices, from reducing digital carbon footprints to supporting meaningful environmental initiatives.</p>

        <h2>Key Takeaways</h2>
        <ul>
          <li>Invest in AI capabilities for personalization and automation</li>
          <li>Build robust first-party data strategies</li>
          <li>Explore immersive technologies that align with your brand</li>
          <li>Integrate sustainability into your marketing approach authentically</li>
        </ul>

        <p>The brands that will thrive in 2025 and beyond are those that embrace these changes while maintaining focus on delivering genuine value to their customers. The fundamentals of good marketing—understanding your audience, communicating clear value, and building trust—remain as important as ever.</p>
      `,
      featured_image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=800&fit=crop',
      category: 'Digital Marketing',
      author_name: 'Channing Hoyle',
      published_at: '2025-01-15',
      reading_time: '5 min read'
    },
    'building-strong-brand-identity': {
      id: '2',
      title: 'Building a Strong Brand Identity: Lessons from 40 Years in Marketing',
      slug: 'building-strong-brand-identity',
      excerpt: 'Key insights on creating lasting brand identities that resonate with customers.',
      content: `
        <p>A strong brand identity is more than a logo or color palette—it's the complete expression of who you are as a business. After four decades of building and refining brands across industries, here are the foundational principles that separate memorable brands from forgettable ones.</p>

        <h2>Start with Strategy, Not Design</h2>
        <p>The most common mistake in brand development is jumping straight to visual design before establishing strategic foundations. Before selecting colors or fonts, you must define your brand positioning, understand your competitive landscape, and articulate your unique value proposition.</p>

        <h2>Consistency Builds Trust</h2>
        <p>Every touchpoint is an opportunity to reinforce your brand. From your website to your packaging, from customer service interactions to trade show presence—consistency across all channels builds the recognition and trust that drive long-term success.</p>

        <h2>Evolution, Not Revolution</h2>
        <p>Strong brands evolve over time while maintaining their core essence. The goal is to stay relevant without abandoning the equity you've built. Think of brand evolution as refinement, not reinvention.</p>

        <h2>Design Systems Scale</h2>
        <p>As your business grows, you need design systems that scale. This means creating comprehensive brand guidelines, flexible visual systems, and clear governance processes that maintain brand integrity across teams and markets.</p>

        <h2>Connect Emotionally</h2>
        <p>The most powerful brands create emotional connections. This doesn't mean being manipulative—it means understanding what your customers truly value and expressing your brand in ways that resonate on a human level.</p>
      `,
      featured_image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&h=800&fit=crop',
      category: 'Brand Strategy',
      author_name: 'Channing Hoyle',
      published_at: '2025-01-10',
      reading_time: '7 min read'
    },
    'business-modern-web-presence': {
      id: '3',
      title: 'Why Every Business Needs a Modern Web Presence',
      slug: 'business-modern-web-presence',
      excerpt: 'In today\'s digital-first world, your website is often the first impression.',
      content: `
        <p>Your website is often the first meaningful interaction a potential customer has with your brand. In an era where digital presence can make or break business opportunities, having a modern, effective website is no longer optional—it's essential.</p>

        <h2>First Impressions Matter</h2>
        <p>Studies show that visitors form opinions about your website within seconds. A dated or poorly designed site immediately raises questions about your business's professionalism and relevance. Conversely, a modern, well-crafted site builds instant credibility.</p>

        <h2>Mobile-First Is Now Standard</h2>
        <p>With mobile devices accounting for the majority of web traffic, your site must perform flawlessly on smartphones and tablets. This isn't just about responsive design—it's about creating experiences optimized for how people actually use their devices.</p>

        <h2>Speed Equals Revenue</h2>
        <p>Page load speed directly impacts both user experience and search rankings. Every second of delay reduces conversions and increases bounce rates. Modern web development prioritizes performance as a core requirement, not an afterthought.</p>

        <h2>Content Should Convert</h2>
        <p>A beautiful website that doesn't generate leads or sales is just a digital brochure. Strategic content, clear calls to action, and conversion-focused design turn visitors into customers.</p>

        <h2>Continuous Improvement</h2>
        <p>The best websites aren't static—they evolve based on data and user feedback. Implementing analytics, testing different approaches, and regularly updating content keeps your site effective over time.</p>
      `,
      featured_image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=1200&h=800&fit=crop',
      category: 'Web Development',
      author_name: 'Channing Hoyle',
      published_at: '2025-01-05',
      reading_time: '4 min read'
    }
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/public/blog/${slug}`);
        setPost(response.data);
      } catch (err) {
        if (defaultPosts[slug]) {
          setPost(defaultPosts[slug]);
        } else {
          navigate('/blog');
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchRelated = async () => {
      try {
        const response = await axios.get(`${API}/public/blog?limit=3`);
        setRelatedPosts(response.data.filter(p => p.slug !== slug).slice(0, 3));
      } catch (err) {
        const related = Object.values(defaultPosts).filter(p => p.slug !== slug).slice(0, 3);
        setRelatedPosts(related);
      }
    };

    fetchPost();
    fetchRelated();
  }, [slug, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="pt-40 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3C4F]"></div>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Article Header */}
      <article className="pt-40 lg:pt-48">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="mb-6">
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1F3C4F] transition-colors"
              data-testid="back-to-blog"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Insights
            </Link>
          </div>

          {post.category && (
            <div className="mb-4">
              <Badge className="bg-[#1F3C4F]">{post.category}</Badge>
            </div>
          )}

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-6">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500 mb-8 pb-8 border-b">
            {post.author_name && (
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author_name}
              </div>
            )}
            {post.published_at && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(post.published_at)}
              </div>
            )}
            {post.reading_time && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {post.reading_time}
              </div>
            )}
          </div>
        </div>

        {/* Featured Image */}
        {post.featured_image && (
          <div className="max-w-4xl mx-auto px-6 lg:px-8 mb-12">
            <img 
              src={post.featured_image}
              alt={post.title}
              className="w-full rounded-2xl shadow-xl"
            />
          </div>
        )}

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-6 lg:px-8 pb-16">
          <div 
            className="blog-content prose prose-lg prose-slate max-w-none text-left
              prose-headings:text-slate-900 prose-headings:font-bold
              prose-h1:text-3xl prose-h1:mt-10 prose-h1:mb-4
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-slate-600 prose-p:leading-relaxed prose-p:mb-4
              prose-a:text-[#1F3C4F] prose-a:underline hover:prose-a:no-underline
              prose-ul:text-slate-600 prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
              prose-ol:text-slate-600 prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6
              prose-li:text-slate-600 prose-li:marker:text-[#1F3C4F] prose-li:my-1
              prose-strong:text-slate-900 prose-strong:font-bold
              prose-blockquote:border-l-4 prose-blockquote:border-[#1F3C4F] prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-slate-500
              prose-code:bg-slate-100 prose-code:px-1 prose-code:rounded
              prose-pre:bg-slate-900 prose-pre:text-slate-100
              [&>*]:mx-0"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Share Section */}
          <div className="mt-12 pt-8 border-t">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2 text-slate-600">
                <Share2 className="w-5 h-5" />
                <span className="font-medium">Share this article</span>
              </div>
              <div className="flex items-center gap-3">
                <a 
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-slate-100 rounded-full hover:bg-[#1F3C4F] hover:text-white transition-colors"
                  data-testid="share-linkedin"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-slate-100 rounded-full hover:bg-[#1F3C4F] hover:text-white transition-colors"
                  data-testid="share-twitter"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-slate-100 rounded-full hover:bg-[#1F3C4F] hover:text-white transition-colors"
                  data-testid="share-facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <button 
                  onClick={() => navigator.clipboard.writeText(shareUrl)}
                  className="p-2 bg-slate-100 rounded-full hover:bg-[#1F3C4F] hover:text-white transition-colors"
                  data-testid="share-copy"
                >
                  <LinkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 lg:py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((related) => (
                <Link 
                  key={related.id} 
                  to={`/blog/${related.slug}`}
                  data-testid={`related-${related.slug}`}
                >
                  <Card className="border-0 shadow-lg overflow-hidden group cursor-pointer hover:shadow-xl transition-shadow">
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img 
                        src={related.featured_image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop'} 
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-6">
                      {related.category && (
                        <Badge variant="outline" className="mb-3">{related.category}</Badge>
                      )}
                      <h3 className="text-lg font-bold text-slate-900 group-hover:text-[#1F3C4F] transition-colors line-clamp-2">
                        {related.title}
                      </h3>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[#1F3C4F]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Elevate Your Marketing?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can apply these strategies to help your business grow.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-[#1F3C4F] hover:bg-slate-100 text-lg px-8 py-6">
              Get in Touch
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default BlogPostPage;
